import { filtersToParams } from 'selectors/app';
import { AllFiltersDefinition } from 'UI/components/templates/SideFiltersLayout/filters';
import { FilterType, MONTHS } from 'UI/constants/defaults';
import { getTodayDate } from 'UI/utils';

import { GPAC_TEAM } from './productionBoard.constants';

export const getExtraFilters = yearFilterOptions => {
  const [currentYear, currentMonth] = getTodayDate().split('-');
  const years = yearFilterOptions.map(year => ({
    id: year,
    title: year.toString()
  }));

  return [
    {
      name: 'years',
      paramName: 'year',
      title: 'Year',
      type: FilterType.Autocomplete,
      getOptionLabel: ({ title }) => title,
      defaultValue: { id: currentYear, title: currentYear },
      options: years
    },
    {
      ...AllFiltersDefinition.find(({ name }) => name === 'months'),
      defaultValue: MONTHS.find(({ extendedId }) => extendedId === currentMonth)
    }
  ];
};

export const getMonthParam = (years, months) => {
  const [currentYear, currentMonth] = getTodayDate().split('-');
  const year = years?.value?.id ?? currentYear;
  const month = months?.value?.extendedId ?? currentMonth;

  return `${year}-${month}-01`;
};

export const formatProductionBoardFilters = (filters = {}) => {
  const { years, months, ...rest } = filters;

  return { ...filtersToParams(rest), month: getMonthParam(years, months) };
};

export const formatProductionResponse = response => {
  if (!response || !Array.isArray(response?.data)) return null;

  const gpacRow = {
    empNo: GPAC_TEAM,
    name: GPAC_TEAM
  };

  const { productionMap, gpacTotal } = response?.data?.reduce(
    (acc, curr) => {
      const { empNo, name, weekOfMonth, total = 0 } = curr;
      if (!empNo && !name && !weekOfMonth) acc.gpacTotal = total;

      const weekKey = weekOfMonth ? `weekOfMonth-${weekOfMonth}` : 'total';

      if (!empNo && !name) {
        gpacRow[weekKey] = total;
        return acc;
      }

      if (!acc.productionMap[empNo]) {
        acc.productionMap[empNo] = { empNo, name: name || empNo };
      }

      acc.productionMap[empNo][weekKey] = total;

      return acc;
    },
    { productionMap: {}, gpacTotal: 0 }
  );

  const productionByWeek = Object.values(productionMap);

  return {
    ...response,
    data: {
      data: [gpacRow, ...productionByWeek],
      total: gpacTotal
    }
  };
};

export const formatRecruiterByWeekResponse = response => {
  if (!response || !Array.isArray(response)) return null;

  const result = response.reduce((acc, curr) => {
    if (!curr.empNo) return acc;

    const weekKey = curr.weekOfMonth ? `weekOfMonth-${curr.weekOfMonth}` : 'total';

    acc[curr.empNo] = {
      ...acc[curr.empNo],
      empNo: curr.empNo,
      name: curr.name,
      [weekKey]: curr.total || 0
    };

    return acc;
  }, {});

  return Object.values(result);
};

export const formatTransactionByWeekResponse = response => {
  if (!response || !Array.isArray(response)) return null;

  return response.map(transaction => ({
    ...transaction,
    name: transaction.transactionName,
    [transaction.weekOfMonth ? `weekOfMonth-${transaction.weekOfMonth}` : 'total']:
      transaction.total || 0
  }));
};

export const formatProductionRosterResponse = response => {
  const rows = response?.data?.teamsCount ?? [];

  const data = [
    {
      managerEmpNo: null,
      team: GPAC_TEAM,
      total: response?.data?.total ?? 0
    },
    ...rows.map(({ managerEmpNo, manager, count }) => ({
      managerEmpNo,
      team: manager,
      total: count
    }))
  ];

  return { ...response, data: { ...response?.data, data } };
};

export const getAvailableDataColumns = ({ columns, data }) => {
  const excludedColumn = 'excluded';
  const firstRow = data[0];

  return columns.map(column => ({
    ...column,
    options: {
      ...column.options,
      display:
        column.options.display === excludedColumn ? excludedColumn : !!firstRow?.[column.name]
    }
  }));
};

export const formatTrackingResponse = response => {
  if (!response || !Array.isArray(response?.data)) return null;

  const gpacRow = {
    empNo: GPAC_TEAM,
    name: GPAC_TEAM,
    mtd: 0,
    ytd: 0
  };

  const data = response.data.map(team => {
    const { empNo, name, mtd, ytd } = team;
    if (!empNo && !name) {
      gpacRow.mtd = mtd || 0;
      gpacRow.ytd = ytd || 0;
      return null;
    }

    return team;
  });

  return {
    ...response,
    data: {
      data: [gpacRow, ...data.filter(Boolean)],
      mtdTracking: gpacRow.mtd,
      ytdTracking: gpacRow.ytd
    }
  };
};
